import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { FormTransformers } from "@/common/utils/formTransformers"
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist"
import { ContactDocumentsSection } from "@/features/establishmentLeadsFunnel/components/ContactDocumentsSection"
import { ContactPersonalInformationSection } from "@/features/establishmentLeadsFunnel/components/ContactPersonalInformationSection"
import { stateStrategy } from "@/features/establishmentLeadsFunnel/constants/stateStrategy.constant"
import { useAdvanceContact } from "@/features/establishmentLeadsFunnel/hooks/useAdvanceContact"
import { useDeleteEstablishmentLeadContact } from "@/features/establishmentLeadsFunnel/hooks/useDeleteEstablishmentLeadContact"
import { useRegressContact } from "@/features/establishmentLeadsFunnel/hooks/useRegressContact"
import { useToggleContactDocument } from "@/features/establishmentLeadsFunnel/hooks/useToggleContactDocument"
import { useUpdateEstablishmentLeadContact } from "@/features/establishmentLeadsFunnel/hooks/useUpdateEstablishmentLeadContact"
import { updateContactSchema, EstablishmentLeadsFunnelUpdateContactValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { ContactData, ContactState } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { ChevronsRight } from "lucide-react"
import { useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import GoBackButton from "@/common/components/GoBackButton";
import { useGoBack } from "@/common/hooks/useGoBack";
import { AxiosResponse } from "axios";
import { leadContactDocumentsDefaultValues } from "@/features/publicCompanyLeadsFunnel/services/leadContactDocumentDefaultValues";
import { useDocumentOptions } from "@/common/hooks/queries/useDocumentOptions";

const Form = ({
  contact,
  establishmentLeadState,
  establishmentLeadName,
  establishmentLeadImageUrl,
}: {
  contact?: ContactData,
  establishmentLeadState: keyof typeof stateStrategy,
  establishmentRestContacts: ContactData[],
  establishmentLeadName: string,
  establishmentLeadImageUrl?: string,
}) => {
  const { establishmentLeadId } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [editContact, setEditContact] = useState(false)
  const [files, setFiles] = useState<{ id: string, file: Blob, fileName: string }[]>([])

  const isInRegistered = establishmentLeadState === 'registered'
  const isInQualifying = establishmentLeadState === 'qualifying'
  const passedEveryRequirement = contact?.requirement_list_summary?.steps?.every((step) => step.passed)
  const hasDisapprovedReview = (contact?.last_review?.state === 'disapproved' || contact?.documents?.some((doc) => doc.last_review?.state === 'disapproved'))

  const { data: documentOptions, isFetched: isDocumentOptionsFetched } = useDocumentOptions();

  const updateLeadOnSuccess = (response: AxiosResponse) => {
    editContact && setEditContact(false)
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })

    const contactDocuments = (response.data.data.contacts as ContactData[]).find(({ id }) => id === (contact?.id || ''))?.documents;

    setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contactDocuments) as any);
  }

  const onToggleDocumentSuccess = (response: AxiosResponse) => {
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] });

    const contactDocuments = (response.data.data.contacts as ContactData[]).find(({ id }) => id === (contact?.id || ''))?.documents;

    setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contactDocuments) as any);
  }

  const redirectOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })
    goBack({ fallback: `/establishment-leads/${establishmentLeadId}/edit` })
  }

  const {
    mutate: toggleDocument,
    isError: toggleDocumentIsError,
  } = useToggleContactDocument({
    contactId: contact?.id,
    establishmentLeadId,
    onSuccess: onToggleDocumentSuccess,
  })

  const {
    mutate: deleteEstablishmentLeadContact,
    isError: deleteEstablishmentLeadContactIsError,
  } = useDeleteEstablishmentLeadContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
  })

  const {
    mutate: regressContact,
    isError: regressContactIsError,
  } = useRegressContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
  })

  const {
    mutate: advanceEstablishmentLeadContact,
    isError: advanceEstablishmentLeadContactIsError,
  } = useAdvanceContact({
    contactId: contact?.id,
    onSuccess: redirectOnSuccess,
  })

  const isServerError = (
    toggleDocumentIsError ||
    deleteEstablishmentLeadContactIsError ||
    regressContactIsError ||
    advanceEstablishmentLeadContactIsError
  )

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors }
  } = useForm<EstablishmentLeadsFunnelUpdateContactValidationSchema>({
    resolver: zodResolver(updateContactSchema),
    defaultValues: {
      contacts: {
        full_name: contact?.full_name,
        email: contact?.email,
        role_in_company: contact?.role_in_company,
        cpf: contact?.cpf,
        phone_attributes: {
          number: contact?.phone?.number,
        },
        responsible_for_company_information: contact?.responsible_for_company_information,
      }
    }
  })

  const {
    mutate: updateContact,
    isPending: updateContactIsPending,
  } = useUpdateEstablishmentLeadContact({
    establishmentLeadId,
    contactId: contact?.id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateContactValidationSchema>({ setError }),
  })

  const onSubmit: SubmitHandler<EstablishmentLeadsFunnelUpdateContactValidationSchema> = (data) => {
    contact?.documents?.forEach((document, index) => {
      if (document.id && data.contacts.documents[index]) {
        data.contacts.documents[index]!.id = document.id
        const file = files.find((file) => file.id === document.id)

        if (file) {
          data.contacts.documents[index]!.file = files.find((file) => file.id === document.id)?.file
        }
      }
    })

    updateContact(data)
  }
  const submit = handleSubmit(onSubmit)

  useEffect(() => {
    if (isDocumentOptionsFetched) {
      setValue('contacts.documents', leadContactDocumentsDefaultValues(documentOptions, contact?.documents) as any);
    }
  }, [isDocumentOptionsFetched, contact?.documents?.[0]?.file?.id]);

  return (
    <form>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback={`/establishment-leads/${establishmentLeadId}/edit`} />
          {establishmentLeadImageUrl ? (
            <img
              src={establishmentLeadImageUrl}
              alt={establishmentLeadName}
              className="size-10 rounded-full object-cover"
            />
          ) : (
            <div className="size-10 bg-white-800 rounded-full"></div>
          )}
          <p className="text-white-400 font-normal">{establishmentLeadName}</p>
          <ChevronsRight color='#ffffffb3' />
          <h2>{contact?.full_name}</h2>
        </div>
        <div className="flex gap-4">
          {(contact?.state === ContactState.pending && !isInRegistered) && (
            <>
              <Button
                type="button"
                variant='outline-error'
                onClick={() => {
                  if (editContact) {
                    reset()
                    setFiles([])
                    setEditContact(false)
                    setValue(`contacts.documents`, leadContactDocumentsDefaultValues(documentOptions, contact.documents) as any);
                  } else {
                    deleteEstablishmentLeadContact()
                  }
                }}
                disabled={updateContactIsPending}
              >
                {editContact ? 'Cancelar' : 'Deletar'}
              </Button>
              {!editContact ? (
                <Button type="button" onClick={() => setEditContact(true)}>Editar</Button>
              ) : (
                <Button type="button" onClick={() => submit()} disabled={updateContactIsPending}>Confirmar</Button>
              )}
            </>
          )}
          {contact?.state === ContactState.pending && (
            <Button type="button" disabled={!passedEveryRequirement || updateContactIsPending || editContact} variant={passedEveryRequirement ? 'default' : 'outline-disabled'} onClick={() => advanceEstablishmentLeadContact()}>Enviar solicitação</Button>
          )}
          {(contact?.state === ContactState.in_review && (isInQualifying)) && (
            <>
              <Button type="button" disabled={!hasDisapprovedReview} variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'} onClick={() => regressContact()}>Solicitar revisão</Button>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-6 w-full">
        <Box className="flex w-full flex-col gap-4 max-h-[calc(100vh-162px)] overflow-y-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {(isServerError) && (
            <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
          )}
          <ContactPersonalInformationSection
            establishmentLeadId={establishmentLeadId}
            contact={contact}
            register={register}
            errors={errors}
            showReviewButtons={contact?.state === ContactState.in_review && isInQualifying}
            updateLeadOnSuccess={updateLeadOnSuccess}
            disableFields={!editContact || updateContactIsPending}
          />
          <ContactDocumentsSection
            establishmentLeadId={establishmentLeadId}
            contact={contact}
            register={register}
            watch={watch}
            errors={errors}
            files={files}
            setFiles={setFiles}
            showReviewButtons={contact?.state === ContactState.in_review && isInQualifying}
            updateLeadOnSuccess={updateLeadOnSuccess}
            toggleDocument={toggleDocument}
            editContact={editContact}
            isInRegistered={isInRegistered}
            updateContactIsPending={updateContactIsPending}
          />
        </Box>
        <Checklist
          requirement_list_summary={isInQualifying ? contact?.requirement_list_summary : undefined}
          className="flex flex-col gap-2 justify-between h-full max-h-[calc(100vh-170px)]"
          Buttons={() => (
            <div className="flex flex-col w-full gap-2 mt-auto mb-[72px]">
              {contact?.state === ContactState.pending && (
                <Button type="button" disabled={!passedEveryRequirement || updateContactIsPending || editContact} variant={passedEveryRequirement ? 'default' : 'outline-disabled'} onClick={() => advanceEstablishmentLeadContact()}>Enviar solicitação</Button>
              )}
              {contact?.state === ContactState.awaiting_client_documents && (
                <Button type="button" disabled variant="outline-disabled">Aguardando</Button>
              )}
              {(contact?.state === ContactState.in_review && isInQualifying) && (
                <>
                  <Button type="button" disabled={!hasDisapprovedReview} variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'} onClick={() => regressContact()}>Solicitar revisão</Button>
                </>
              )}
            </div>
          )}
        />
      </div>
    </form>
  )
}

export default Form
